import dayjs from "dayjs";
import { IServiceAccount, IUserDetails, ProfileActionType, User } from "../models/interfaces";

export const InitialDetails: IUserDetails = {
	name: "",
	email: "",
	emp_id: "",
	profiles: [],
	profile_access: [],
	roles: [],
	id: "",
	access_expires_at: null,
	is_superadmin: false,
};

export const InitialServiceAccountDetails: IServiceAccount = {
	id: "",
	name: "",
	description: "",
	profile_access: [],
	roles: [],
	enable_access: true,
	email: "",
	is_service_account: true,
	collaborators: [],
};

export const InitialStudyFilters = { search: null, selectedFilters: [], selectedStudyTypes: [], isProfileFiltered: false, isStudyTypeFiltered: false, sort_ascending: null };
export const InitialStudyPayload = {
	currentPage: 1,
	pageSize: 10,
	totalPages: null,
	studiesData: [],
};

export const profileActionTitles = {
	[ProfileActionType.Add]: "Add Profile",
	[ProfileActionType.Edit]: "Edit Profile",
	[ProfileActionType.Delete]: "Delete Profile",
};

export function debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
	let timeoutId: NodeJS.Timeout | null = null;

	return (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			func(...args);
		}, delay);
	};
}

export const transformPayload = (record: User) => {
	return { emp_id: record.collaborator_id, email: record.collaborator_email, profiles: record.profiles };
};

export const transformPayloadEditDetails = (record: User): IUserDetails => {
	return {
		email: record.collaborator_email,
		name: record.collaborator_name,
		profiles: record.profiles,
		profile_access: record.profile_access,
		roles: record.roles,
		emp_id: record.collaborator_id,
		// Surfaced as read context for the edit modal. On submit, the same field name carries the
		// admin's picked expiry date back to the BE (YYYY-MM-DD on PUT, ISO timestamp on GET).
		access_expires_at: record.access_expires_at ?? null,
		is_superadmin: record.is_superadmin ?? false,
	};
};

export const transformPayloadDelete = (record: User) => {
	return { email: record.collaborator_email, delete: true };
};

export const datesIntersect = (start1: string | null, end1: string | null, start2: string | null, end2: string | null): boolean => {
	// If any version has no dates, treat it as infinite range
	if (!start1 && !end1) return true;
	if (!start2 && !end2) return true;

	const s1 = start1 ? dayjs(start1) : dayjs("1900-01-01");
	const e1 = end1 ? dayjs(end1) : dayjs("9999-12-31");
	const s2 = start2 ? dayjs(start2) : dayjs("1900-01-01");
	const e2 = end2 ? dayjs(end2) : dayjs("9999-12-31");

	return !(e1.isBefore(s2) || s1.isAfter(e2));
};

export const toTitleCase = (str: string) => {
	return str
		.replace(/_/g, " ")
		.toLowerCase()
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

export const arraysEqual = (a, b) => {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (a.length !== b.length) return false;

	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};
// escapeHtml is centralized in src/utils/security; re-exported here so existing
// import paths continue to work while sharing a single implementation.
export { escapeHtml } from "src/utils/security";

/**
 * Parses `filename` / `filename*` out of a Content-Disposition header.
 * Returns null if the header is absent or unparseable.
 *
 * Handles both:
 *   attachment; filename="audit_logs_2024.xlsx"
 *   attachment; filename*=UTF-8''audit%20logs%202024.xlsx
 */
export function parseFilename(header: string | undefined): string | null {
	if (!header) return null;

	// RFC 5987 encoded filename takes priority
	const rfc5987 = /filename\*\s*=\s*(?:[^']*'')?([^;]+)/i.exec(header);
	if (rfc5987) {
		try {
			return decodeURIComponent(rfc5987[1].trim());
		} catch {
			// fall through to plain filename
		}
	}

	const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
	return plain ? plain[1].trim() : null;
}
